import { BehaviorSubject } from "rxjs";
import produce from "immer";

import { fetchWrapper } from "data/FetchWrapper";
import { topicService, searchService, alertService } from "services";

// import { history } from 'data/History';

const query$ = new BehaviorSubject(null);
const abort$ = new BehaviorSubject(new AbortController());
const baseUrl = `/api/query`;

const create = () => {
  const query = produce({}, (query) => {
    query.parameters = {
      ...searchService.search$.value,
    };
    query.report = { ...searchService.result$.value, resultset: [] };
    const current = topicService.current$.value;
    query.topicId = (current && current.id) || null;
  });
  return fetchWrapper
    .post(`${baseUrl}`, query, abort$.value.signal)
    .then((query) => {
      updateTopicQuery();
      query$.next(query);
      return query;
    });
};

const current = (q) => query$.next(q);

const edit = (q) => {
  const query = produce(q, (query) => {
    query.parameters = {
      ...searchService.search$.value,
    };
    query.report = { ...searchService.result$.value, resultset: [] };
  });
  return fetchWrapper
    .put(`${baseUrl}/${query.id}`, query, abort$.value.signal)
    .then((query) => {
      updateTopicQuery();
      query$.next(query);
      return query;
    });
};

const _response = (response) => {
  const query = query$.value;
  // If no query => save current search and add query
  if (!query) {
    return create().then(() => {
      return _response(response);
    });
  }
  const _response_ = query.report.resultset.find((r) => r.id === response.id);
  let request;
  if (_response_ == null) {
    request = fetchWrapper.post(
      `${baseUrl}/${query.id}/${response.responseType.toLowerCase()}`,
      response,
      abort$.value.signal
    );
  } else {
    request = fetchWrapper.delete(
      `${baseUrl}/${query.id}/${response.responseType.toLowerCase()}/${
        response.id
      }`,
      null,
      abort$.value.signal
    );
  }
  return request
    .then((r) => {
      const query = produce(query$.value, (query) => {
        let resultset = query.report.resultset;
        if (r.id) {
          resultset.push(r);
        } else {
          resultset = resultset.filter((r) => r.id !== response.id);
        }
        query.report.resultset = resultset;
      });
      updateTopicQuery();
      query$.next(query);
      return r;
    })
    .catch((e) => alertService.error(e));
};

const file = (formData) => {
  const query = query$.value;
  return fetchWrapper
    .upload(`${baseUrl}/${query.id}/file`, formData, abort$.value.signal)
    .then((r) => {
      const query = produce(query$.value, (query) => {
        let resultset = query.report.resultset;
        resultset.push(r);
        query.report.resultset = resultset;
      });
      updateTopicQuery();
      query$.next(query);
      return r;
    });
};

const download = (fileId, fileName) => {
  fetchWrapper
    .download(`/api/file/${fileId}`, abort$.value.signal)
    .then((blob) => {
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
    })
    .catch((e) => alertService.error(e));
};

const note = (responseId, note) => {
  const query = query$.value;
  const response = produce(
    query.report.resultset.find((r) => r.id === responseId),
    (response) => {
      response.note = note;
    }
  );
  return fetchWrapper
    .put(
      `${baseUrl}/${
        query.id
      }/${response.responseType.toLowerCase()}/${responseId}`,
      response,
      abort$.value.signal
    )
    .then((response) => {
      const query = produce(query$.value, (query) => {
        const index = query.report.resultset.findIndex(
          (r) => r.id === responseId
        );
        query.report.resultset[index] = response;
      });
      updateTopicQuery();
      query$.next(query);
      return response;
    })
    .catch((e) => alertService.error(e));
};

const topic = (topicId) => {
  const query = query$.value;
  let request;
  if (query.topicId === topicId) {
    request = fetchWrapper.delete(
      `${baseUrl}/${query.id}/topic`,
      null,
      abort$.value.signal
    );
  } else {
    request = fetchWrapper.put(
      `${baseUrl}/${query.id}/topic`,
      { topicId },
      abort$.value.signal
    );
  }
  return request
    .then((query) => {
      updateTopicQuery();
      query$.next(query);
      return query;
    })
    .catch((e) => alertService.error(e));
};

const _delete = (queryId) => {
  return fetchWrapper
    .delete(`${baseUrl}/${queryId}`, null, abort$.value.signal)
    .then((query) => {
      updateTopicQuery();
      query$.next(null);
    })
    .catch((e) => alertService.error(e));
};

const updateTopicQuery = () =>
  topicService.current(topicService.current$.value);

const abort = () => {
  abort$.value.abort();
  abort$.next(new AbortController());
};

export const queryService = {
  create,
  current,
  edit,
  response: _response,
  file,
  download,
  note,
  topic,
  delete: _delete,
  query$,
  abort,
};
